import React, { useEffect, useState } from 'react';
import { X, Share2, Plus, Smartphone, Bell } from 'lucide-react';
import { Button } from './ui/button';
import {
  registerPWA, promptInstall, canPromptInstall, isStandalone, isIOS,
  enablePushNotifications,
} from '../utils/pwa';

const DISMISS_KEY = 'lh-install-prompt-dismissed-at';
const DISMISS_DAYS = 14;

const dismissedRecently = () => {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!ts) return false;
    return (Date.now() - ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (_e) { return false; }
};

/**
 * Bottom-anchored install prompt — appears once per user, polite, dismissible.
 *   - Android/Chrome: native install dialog via beforeinstallprompt event
 *   - iOS Safari:    custom guide pointing at Share → Add to Home Screen
 *   - If already installed (display-mode: standalone): renders nothing
 */
const InstallPrompt = () => {
  const [show, setShow] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);
  const [showPush, setShowPush] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      // Already installed → offer push notifications opt-in instead
      if (Notification.permission === 'default' && localStorage.getItem('token')) {
        setShowPush(true);
      }
      return undefined;
    }
    if (dismissedRecently()) return undefined;
    registerPWA({
      onInstallable: () => setShow(true),
    });
    if (isIOS() && !canPromptInstall()) {
      // Safari never fires beforeinstallprompt → show our custom guide
      const t = setTimeout(() => setShow(true), 2500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
    setShow(false);
    setIosOpen(false);
  };

  const onInstallClick = async () => {
    if (isIOS()) {
      setIosOpen(true);
      return;
    }
    const outcome = await promptInstall();
    if (outcome === 'accepted' || outcome === 'unavailable') dismiss();
  };

  const onEnablePush = async () => {
    setShowPush(false);
    await enablePushNotifications().catch(() => {});
  };
  const dismissPush = () => {
    setShowPush(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
  };

  // Push prompt (only when already installed)
  if (showPush) {
    return (
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-[360px] z-50 animate-in slide-in-from-bottom"
           data-testid="push-opt-in-prompt">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 flex items-start gap-3">
          <div className="bg-purple-100 rounded-xl p-2 flex-shrink-0">
            <Bell className="h-5 w-5 text-[#7042cc]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-900">Get chat alerts on your phone</p>
            <p className="text-xs text-gray-600 mt-0.5">
              We'll only notify you when an agent (or buyer) actually messages you.
            </p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={onEnablePush}
                      className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
                      data-testid="push-opt-in-enable">
                Turn on
              </Button>
              <Button size="sm" variant="ghost" onClick={dismissPush}
                      data-testid="push-opt-in-dismiss">
                Not now
              </Button>
            </div>
          </div>
          <button onClick={dismissPush} className="text-gray-400 hover:text-gray-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (!show) return null;

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-[380px] z-50 animate-in slide-in-from-bottom"
           data-testid="install-prompt">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 flex items-start gap-3">
          <div className="bg-purple-100 rounded-xl p-2 flex-shrink-0">
            <Smartphone className="h-5 w-5 text-[#7042cc]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-900">
              Install lovinahome on your home screen
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              Faster than the website. Get chat alerts the moment an agent replies.
            </p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={onInstallClick}
                      className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
                      data-testid="install-prompt-install">
                {isIOS() ? 'Show me how' : 'Install'}
              </Button>
              <Button size="sm" variant="ghost" onClick={dismiss}
                      data-testid="install-prompt-dismiss">
                Maybe later
              </Button>
            </div>
          </div>
          <button onClick={dismiss} className="text-gray-400 hover:text-gray-700"
                  aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* iOS install instructions sheet */}
      {iosOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
             onClick={dismiss}
             data-testid="ios-install-sheet">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-3 text-gray-900">Add to Home Screen</h3>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="bg-purple-100 text-[#7042cc] rounded-full w-6 h-6 flex items-center justify-center font-semibold flex-shrink-0">1</span>
                <span>Tap the <Share2 className="inline h-4 w-4 mx-0.5 text-blue-600" /> <strong>Share</strong> button at the bottom of Safari</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-purple-100 text-[#7042cc] rounded-full w-6 h-6 flex items-center justify-center font-semibold flex-shrink-0">2</span>
                <span>Scroll down and tap <Plus className="inline h-4 w-4 mx-0.5" /> <strong>Add to Home Screen</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-purple-100 text-[#7042cc] rounded-full w-6 h-6 flex items-center justify-center font-semibold flex-shrink-0">3</span>
                <span>Tap <strong>Add</strong> in the top-right. The lovinahome icon will appear on your home screen.</span>
              </li>
            </ol>
            <Button onClick={dismiss} className="w-full mt-5 bg-[#7042cc] hover:bg-[#5b35a8] text-white"
                    data-testid="ios-install-sheet-done">
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
